import { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CATEGORY_META, type ItineraryDay, type ItineraryStop, type ListItem } from '@travel-planner/shared';
import { useMapContext } from '../map/MapContext';
import { useAddStop, useDeleteStop, useItinerary, useReorderStops, useUpdateStop } from './useItinerary';

const STOP = 'stop:';
const DAY = 'day:';
const POI = 'poi:';

// Monday-first weekday labels, matching weekdayIndex() below.
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function utcDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function formatDate(iso: string): string {
  return utcDate(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function monthLabel(iso: string): string {
  return utcDate(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// 0 = Monday … 6 = Sunday, so the calendar week starts on Monday.
function weekdayIndex(iso: string): number {
  return (utcDate(iso).getUTCDay() + 6) % 7;
}

// Group contiguous, chronologically-sorted days into calendar months.
function groupByMonth(days: ItineraryDay[]): { key: string; label: string; days: ItineraryDay[] }[] {
  const groups: { key: string; label: string; days: ItineraryDay[] }[] = [];
  for (const d of days) {
    const key = d.date.slice(0, 7); // YYYY-MM
    let cur = groups[groups.length - 1];
    if (!cur || cur.key !== key) {
      cur = { key, label: monthLabel(d.date), days: [] };
      groups.push(cur);
    }
    cur.days.push(d);
  }
  return groups;
}

export function Itinerary({ tripId }: { tripId: string }) {
  const { data: days = [], isLoading } = useItinerary(tripId);
  const { placedItems, showRoute, focus } = useMapContext();
  const addStop = useAddStop(tripId);
  const reorder = useReorderStops(tripId);

  const [local, setLocal] = useState<ItineraryDay[]>(days);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [originDate, setOriginDate] = useState<string | null>(null);

  useEffect(() => {
    if (activeId === null) setLocal(days);
  }, [days, activeId]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const dateOfStop = (stopId: string): string | null =>
    local.find((d) => d.stops.some((s) => s.id === stopId))?.date ?? null;

  const dateOfOver = (overId: string): string | null => {
    if (overId.startsWith(DAY)) return overId.slice(DAY.length);
    if (overId.startsWith(STOP)) return dateOfStop(overId.slice(STOP.length));
    return null;
  };

  function onDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    setActiveId(id);
    if (id.startsWith(STOP)) setOriginDate(dateOfStop(id.slice(STOP.length)));
  }

  // Live move of a stop between day cells.
  function onDragOver(e: DragOverEvent) {
    const id = String(e.active.id);
    if (!id.startsWith(STOP)) return; // palette POIs handled on drop only
    const stopId = id.slice(STOP.length);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;
    const from = dateOfStop(stopId);
    const to = dateOfOver(overId);
    if (!from || !to || from === to) return;

    setLocal((prev) => {
      const fromDay = prev.find((d) => d.date === from);
      const toDay = prev.find((d) => d.date === to);
      if (!fromDay || !toDay) return prev;
      const stop = fromDay.stops.find((s) => s.id === stopId);
      if (!stop) return prev;
      const overIndex = overId.startsWith(STOP)
        ? toDay.stops.findIndex((s) => s.id === overId.slice(STOP.length))
        : toDay.stops.length;
      const insertAt = overIndex < 0 ? toDay.stops.length : overIndex;
      return prev.map((d) => {
        if (d.date === from) return { ...d, stops: d.stops.filter((s) => s.id !== stopId) };
        if (d.date === to)
          return {
            ...d,
            stops: [...d.stops.slice(0, insertAt), stop, ...d.stops.slice(insertAt)],
          };
        return d;
      });
    });
  }

  function onDragEnd(e: DragEndEvent) {
    const id = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    const started = originDate;
    setActiveId(null);
    setOriginDate(null);
    if (!overId) {
      setLocal(days);
      return;
    }

    // Dropping a POI from the palette schedules it on a day.
    if (id.startsWith(POI)) {
      const date = dateOfOver(overId);
      if (date) addStop.mutate({ date, listItemId: id.slice(POI.length) });
      return;
    }

    // Reordering / moving a stop.
    const stopId = id.slice(STOP.length);
    const destDate = dateOfStop(stopId);
    if (!destDate) return;
    const destDay = local.find((d) => d.date === destDate);
    if (!destDay) return;
    const oldIndex = destDay.stops.findIndex((s) => s.id === stopId);
    const newIndex = overId.startsWith(STOP)
      ? destDay.stops.findIndex((s) => s.id === overId.slice(STOP.length))
      : destDay.stops.length - 1;
    if (oldIndex < 0 || newIndex < 0) return;

    const next = local.map((d) =>
      d.date === destDate ? { ...d, stops: arrayMove(d.stops, oldIndex, newIndex) } : d,
    );
    setLocal(next);

    const destStops = next.find((d) => d.date === destDate)!.stops.map((s) => s.id);
    reorder.mutate({ date: destDate, orderedStopIds: destStops });
    if (started && started !== destDate) {
      const srcStops = next.find((d) => d.date === started)?.stops.map((s) => s.id) ?? [];
      reorder.mutate({ date: started, orderedStopIds: srcStops });
    }
  }

  if (isLoading) return <p className="text-secondary">Loading itinerary…</p>;
  if (days.length === 0)
    return <p className="text-secondary">Set the trip start/end dates to build a day timeline.</p>;

  const activePoi = activeId?.startsWith(POI)
    ? placedItems.find((p) => p.id === activeId.slice(POI.length))
    : null;
  const activeStop = activeId?.startsWith(STOP)
    ? local.flatMap((d) => d.stops).find((s) => s.id === activeId.slice(STOP.length))
    : null;

  const months = groupByMonth(local);

  return (
    <div>
      <h2 className="h4 mb-2">Itinerary</h2>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setOriginDate(null);
          setLocal(days);
        }}
      >
        <Palette items={placedItems} />
        <div className="d-flex flex-column gap-4 mt-3">
          {months.map((m) => {
            const firstDay = m.days[0];
            const lead = firstDay ? weekdayIndex(firstDay.date) : 0;
            return (
              <div key={m.key}>
                <h3 className="h6 text-secondary mb-2">{m.label}</h3>
                <div style={{ overflowX: 'auto' }}>
                  <div className="tp-calendar">
                    {WEEKDAYS.map((w) => (
                      <div key={w} className="tp-cal-weekday">
                        {w}
                      </div>
                    ))}
                    {Array.from({ length: lead }).map((_, i) => (
                      <div key={`blank-${i}`} aria-hidden />
                    ))}
                    {m.days.map((day) => (
                      <DayCell
                        key={day.date}
                        day={day}
                        tripId={tripId}
                        onShowRoute={() =>
                          showRoute(
                            formatDate(day.date),
                            day.stops
                              .filter((s) => s.lat !== null && s.lng !== null)
                              .map((s) => ({ lng: s.lng as number, lat: s.lat as number, name: s.title })),
                          )
                        }
                        onFocusStop={(listItemId) => focus(listItemId)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DragOverlay>
          {activePoi ? (
            <PaletteChipView item={activePoi} />
          ) : activeStop ? (
            <div style={{ ...stopBaseStyle, boxShadow: 'var(--shadow-lg)', fontSize: '0.76rem' }}>
              {CATEGORY_META[activeStop.category].icon} {activeStop.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function Palette({ items }: { items: ListItem[] }) {
  if (items.length === 0)
    return (
      <p className="text-body-tertiary small">
        Place items on the map first — then drag them onto a day.
      </p>
    );
  return (
    <div>
      <div className="text-secondary mb-1" style={{ fontSize: '0.8rem' }}>
        Located items — drag onto a day:
      </div>
      <div className="d-flex gap-2 flex-wrap">
        {items.map((i) => (
          <PaletteChip key={i.id} item={i} />
        ))}
      </div>
    </div>
  );
}

function PaletteChip({ item }: { item: ListItem }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${POI}${item.id}` });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ ...chipStyle, opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
    >
      <span aria-hidden>{CATEGORY_META[item.category].icon}</span>
      {item.title}
    </div>
  );
}

function PaletteChipView({ item }: { item: ListItem }) {
  return (
    <div style={{ ...chipStyle, boxShadow: 'var(--shadow-lg)' }}>
      <span aria-hidden>{CATEGORY_META[item.category].icon}</span>
      {item.title}
    </div>
  );
}

// A single calendar day: a droppable cell holding its (sortable) stops.
function DayCell({
  day,
  tripId,
  onShowRoute,
  onFocusStop,
}: {
  day: ItineraryDay;
  tripId: string;
  onShowRoute: () => void;
  onFocusStop: (listItemId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${DAY}${day.date}` });
  const dayNum = Number(day.date.slice(8, 10));
  return (
    <div
      ref={setNodeRef}
      className="tp-cal-day"
      style={{
        background: isOver ? 'var(--drop-hover)' : 'var(--surface)',
        outline: isOver ? '2px solid var(--accent)' : '2px solid transparent',
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>
          {dayNum}
        </span>
        {day.stops.length > 0 && (
          <button
            onClick={onShowRoute}
            title="Show route on map"
            className="btn btn-ghost btn-sm p-0 px-1"
            style={{ minHeight: 'auto', fontSize: '0.68rem', color: 'var(--accent)' }}
          >
            ↗ {day.stops.length}
          </button>
        )}
      </div>
      <SortableContext items={day.stops.map((s) => `${STOP}${s.id}`)} strategy={verticalListSortingStrategy}>
        <div className="d-flex flex-column gap-1">
          {day.stops.map((s) => (
            <StopChip key={s.id} stop={s} tripId={tripId} onFocus={() => onFocusStop(s.listItemId)} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

// Compact stop card sized for a calendar cell. Keeps time editing, focus,
// delete and drag-to-reorder.
function StopChip({
  stop,
  tripId,
  onFocus,
}: {
  stop: ItineraryStop;
  tripId: string;
  onFocus: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${STOP}${stop.id}`,
  });
  const update = useUpdateStop(tripId);
  const del = useDeleteStop(tripId);

  return (
    <div
      ref={setNodeRef}
      style={{
        ...stopBaseStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <div className="d-flex align-items-center gap-1">
        <button
          {...attributes}
          {...listeners}
          aria-label="Drag"
          className="btn btn-ghost btn-sm p-0 text-body-tertiary"
          style={{ cursor: 'grab', minHeight: 'auto', lineHeight: 1, touchAction: 'none' }}
        >
          ⠿
        </button>
        <button
          onClick={onFocus}
          title="Show on map"
          className="btn btn-ghost btn-sm p-0 flex-grow-1 text-start"
          style={{ minHeight: 'auto', minWidth: 0 }}
        >
          <span className="d-block text-truncate" style={{ fontSize: '0.76rem' }}>
            {CATEGORY_META[stop.category].icon} {stop.title}
          </span>
        </button>
        <button
          onClick={() => del.mutate(stop.id)}
          title="Remove from day"
          className="btn btn-ghost btn-sm p-0 text-danger"
          style={{ minHeight: 'auto', lineHeight: 1 }}
        >
          ✕
        </button>
      </div>
      <input
        type="time"
        className="form-control form-control-sm mt-1"
        value={stop.time ?? ''}
        onChange={(e) => update.mutate({ stopId: stop.id, patch: { time: e.target.value || null } })}
        style={{ fontSize: '0.72rem', padding: '1px 4px', height: 'auto' }}
      />
    </div>
  );
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 11px',
  borderRadius: 999,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  fontSize: '0.82rem',
  cursor: 'grab',
};

const stopBaseStyle: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 5,
};
